/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';
import { PlaybackContentType } from '../../core/socu/enums/PlaybackContentType';
import { RootState } from '../../store/root.reducers';
import { VideoManager } from '../../core/videoplayer';
import { DeviceModel } from '../../core/device';
import { Mousetrap } from '../common';
import { ImageLoader } from '../ImageLoader';
import { ImageLoaderTypes } from '../ImageLoader/ImageLoaderTypes';
import { MouseOperations } from '../../core/navigation/MouseOperations';
import {
    PlaybackRestrictionType,
    PlayerViewMode,
    VideoPlayerStates,
} from '../../core/videoplayer/enums';
import { IPlayerStatus } from '../../core/videoplayer/interfaces';
import { Log, VuLogger } from '../../core/logging';
import { Keys } from '../../core/navigation/Keys';
import { VisibilityState } from '../../store/visibilityevents/visibilitystate';
import { SocuDataModelImpl } from '../../core/socu/impls';
import { QoEAppLogger } from '../../core/logging/qoe/QoEAppLogger';
import { QoEConnectionModeType } from '../../core/logging/qoe/QoEConnectionModeType';
import { resetPlaybackParamsVideoplayer } from '../../store/videoplayer/videoplayer.reducers';
import { ServicesClientImpl } from '../../core/impls';
import { Channel } from '../../core/entities/Channel';
import { videoPlayerStartPlaybackAction } from '../../store/videoplayer/videoplayer.actions';

import './VideoPlayer.scss';

const VideoPlayer: FunctionComponent = (): JSX.Element => {
    const ID = 'src/views/VideoPlayer/VideoPlayer.tsx';
    const TAG = 'Videoplayer';
    const unmuted = DeviceModel.isWebOsPlatform() || DeviceModel.isTizenPlatform();

    const dispatch = useDispatch();
    const uiContainerRef = useRef<HTMLDivElement>(null);
    const videoRef = useRef(null);

    // useSelectors
    const { eventType, identity, key, value } = useSelector(
        (state: RootState) => state.inputFactory,
    );
    const { isActive: isAntiburnOsdActive } = useSelector((state: RootState) => state.antiburnOsd);
    const { visibilityState } = useSelector((state: RootState) => state.visibilityEvents);

    // useStates
    const [isBlackSurfaceActive, setIsBlackSurfaceActive] = useState(false);
    const [isMediaPauseIconVisible, setIsMediaPauseIconVisible] = useState(false);
    const [isVideoPaused, setIsVideoPaused] = useState(false);
    const [videoWindowIcon, setVideoWindowIcon] = useState(
        ImageLoaderTypes.MEDIA_HIGHLIGHTED_PAUSE_ICON,
    );

    VideoManager.mIsAntiburnActive = isAntiburnOsdActive;

    const handleLocationRestriction = () => {
        VideoManager.getInstance().stop();
        setIsBlackSurfaceActive(true);
    };

    const handleNetworkConnection = () => {
        setIsBlackSurfaceActive(!isAntiburnOsdActive);
    };

    const handleNetworkDisconnection = () => {
        setIsBlackSurfaceActive(true);
    };

    const handleOnProgramBoundary = () => {
        loggingsOnProgramBoundary();
    };

    const loggingsOnProgramBoundary = () => {
        QoEAppLogger.logProgramEnded();
        VuLogger.logWatchStoppedEvent(VuLogger.WatchStoppedEndReason.END_OF_CONTENT);
    };

    const handlePlaybackError = () => {
        Log.e(TAG, 'handlePlaybackError: Playback stopped due to an error.');
        VideoManager.getInstance().stop();
    };

    const handlePlaybackRestriction = (type: PlaybackRestrictionType) => {
        // TODO: Will be able to handle parental control usecase in future.
        Log.d(TAG, 'handlePlaybackRestriction type is', type);
        switch (type) {
            case PlaybackRestrictionType.Adult:
                setIsBlackSurfaceActive(true);
                break;

            case PlaybackRestrictionType.NetworkConnected:
                handleNetworkConnection();
                break;

            case PlaybackRestrictionType.NetworkDisconnected:
                handleNetworkDisconnection();
                break;

            case PlaybackRestrictionType.OutOfRegion:
                logDeviceRestrictionChange(type);
                handleLocationRestriction();
                break;
            case PlaybackRestrictionType.Blacklisted:
            case PlaybackRestrictionType.OutOfHome:
                handleLocationRestriction();
                break;

            case PlaybackRestrictionType.NoRestriction:
                setIsBlackSurfaceActive(false);
                break;

            default:
        }
    };

    const handleVideoStateChange = (status: IPlayerStatus): void => {
        Log.d(TAG, 'handleVideoStateChange status is', status);
        switch (status.state) {
            case VideoPlayerStates.PAUSE:
                setIsVideoPaused(true);
                break;
            case VideoPlayerStates.PLAYING:
                setIsVideoPaused(false);
                setIsMediaPauseIconVisible(false);
                break;
            default:
                Log.d(TAG, 'The state of the video player is unknown.');
        }
    };

    const handlePlayerViewModeChange = (playerViewMode: PlayerViewMode) => {
        Log.d(TAG, 'handlePlayerViewModeChange playerVewMode: ', playerViewMode);
        switch (playerViewMode) {
            case PlayerViewMode.FullScreen:
                setIsMediaPauseIconVisible(false);
                break;
            case PlayerViewMode.VideoWindow:
                onPlayerMinimized();
                break;
            default:
        }
    };

    const handleVisibilityChange = (state: VisibilityState) => {
        const playerViewMode = VideoManager.getInstance().getPlayerViewMode();
        if (state === VisibilityState.ON_PAUSE) {
            QoEAppLogger.logVisibilityBackground();
            stopVideoPlayback();
            return;
        }
        if (playerViewMode === PlayerViewMode.FullScreen)
            dispatch(resetPlaybackParamsVideoplayer());

        if (
            !DeviceModel.getAppSettings().isVideoWindowEnabled() &&
            playerViewMode === PlayerViewMode.VideoWindow
        )
            return;

        startVideoPlayback();
    };

    // TODO: We should move this function to QoE logger.
    const logDeviceRestrictionChange = (type: PlaybackRestrictionType) => {
        if (type !== PlaybackRestrictionType.OutOfRegion) {
            QoEAppLogger.updateConfigurationForConnectionMode(QoEConnectionModeType.LOCAL);
            Diagnostics.info(TAG, 'logDeviceRestrictionChange: inRegion');
            Log.i(TAG, 'logDeviceRestrictionChange: inRegion');
        } else {
            QoEAppLogger.updateConfigurationForConnectionMode(QoEConnectionModeType.AWAY);
            Diagnostics.info(TAG, 'logDeviceRestrictionChange: outOfRegion');
            Log.i(TAG, 'logDeviceRestrictionChange: outOfRegion');
        }
    };

    const onKeyPlayPauseVideoPlayer = () => {
        togglePlaybackState();
    };

    const onMouseClickVideoPlayer = () => {
        togglePlaybackState();
    };

    const onMouseHoverVideoPlayer = () => {
        if (!isVideoPaused) setVideoWindowIcon(ImageLoaderTypes.MEDIA_HIGHLIGHTED_PAUSE_ICON);
        else setVideoWindowIcon(ImageLoaderTypes.MEDIA_PAUSE_ICON);
    };

    const onPlayerMinimized = () => {
        const isVideoPlaying = VideoManager.getInstance().isPlaying();
        const hasError = VideoManager.getInstance().hasPlayerError();
        const hasRestriction = VideoManager.getInstance().isChannelRestricted();
        if (!isVideoPlaying && !hasError && !hasRestriction) setIsMediaPauseIconVisible(true);
    };

    const startVideoPlayback = () => {
        const lastTunedChannelNumber = DeviceModel.getAppSettings().getLastTunedChannelNumber();
        if (!lastTunedChannelNumber) return;
        new ServicesClientImpl().tuneToChannel(lastTunedChannelNumber).then((channel: Channel) => {
            dispatch(videoPlayerStartPlaybackAction(channel));
            VideoManager.getInstance().startPlayback({
                channel,
                playbackContentType: PlaybackContentType.LIVE,
            });
        });
    };

    const stopVideoPlayback = () => {
        if (SocuDataModelImpl.getInstance().isSocu()) {
            VideoManager.getInstance().updateLastViewedPosition();
            SocuDataModelImpl.getInstance().resetSocuData();
        }
        VideoManager.getInstance().stop();
        VuLogger.logWatchStoppedEvent(VuLogger.WatchStoppedEndReason.VISIBILITY_BEHIND);
    };

    const togglePlaybackState = () => {
        const canPause =
            !isVideoPaused &&
            VideoManager.getInstance().getPlayerViewMode() !== PlayerViewMode.FullScreen &&
            VideoManager.getInstance().getVideoPlayerModel().trickModeRestrictions()?.allowPause;
        if (canPause) {
            setIsMediaPauseIconVisible(true);
            setIsVideoPaused(true);
            setVideoWindowIcon(ImageLoaderTypes.MEDIA_PAUSE_ICON);
            VideoManager.getInstance().pause(true);
            return;
        }
        if (
            isVideoPaused &&
            VideoManager.getInstance().getPlayerViewMode() !== PlayerViewMode.FullScreen
        ) {
            setIsMediaPauseIconVisible(false);
            setIsVideoPaused(false);
            setVideoWindowIcon(ImageLoaderTypes.MEDIA_HIGHLIGHTED_PAUSE_ICON);
            VideoManager.getInstance().unPause();
        }
    };

    useEffect(() => {
        const refPlaybackErrorListner =
            VideoManager.getInstance().addPlaybackErrorListener(handlePlaybackError);
        const refPlaybackRestrictionHandler =
            VideoManager.getInstance().addPlaybackRestrictionListener(handlePlaybackRestriction);
        const refProgramBoundaryHandler =
            VideoManager.getInstance().addProgramBoundaryListener(handleOnProgramBoundary);
        const refViewModeChangeListener =
            VideoManager.getInstance().addPlayerViewModeChangeListener(handlePlayerViewModeChange);
        const refVideoStateHandler =
            VideoManager.getInstance().addVideoPlayerStateChangeListener(handleVideoStateChange);
        return () => {
            VideoManager.getInstance().removePlaybackErrorListener(refPlaybackErrorListner);
            VideoManager.getInstance().removePlaybackRestrictionListner(
                refPlaybackRestrictionHandler,
            );
            VideoManager.getInstance().removePlayerViewModeChangeListner(refViewModeChangeListener);
            VideoManager.getInstance().removeProgramBoundaryListener(refProgramBoundaryHandler);
            VideoManager.getInstance().removeVideoPlayerStateChangeListener(refVideoStateHandler);
        };
    }, []);

    useEffect(() => {
        if (
            DeviceModel.getAppSettings().isVideoWindowEnabled() &&
            visibilityState === VisibilityState.ON_RESUME
        ) {
            if (isAntiburnOsdActive) {
                stopVideoPlayback();
            } else {
                startVideoPlayback();
            }
        }

        if (DeviceModel.getAppSettings().isVideoWindowEnabled()) {
            uiContainerRef?.current?.classList.toggle('hidden', isAntiburnOsdActive);
        }
    }, [isAntiburnOsdActive]);

    useEffect(() => {
        if (videoRef.current && uiContainerRef.current) {
            const oVidManager: VideoManager = VideoManager.getInstance();
            oVidManager.configurePlayer(videoRef.current, uiContainerRef.current);
        }
    }, [uiContainerRef, videoRef]);

    useEffect(() => {
        if (value !== ID) return;
        switch (eventType) {
            case MouseOperations.MOUSE_CLICK:
                onMouseClickVideoPlayer();
                break;

            case MouseOperations.MOUSE_OUT:
                setIsMediaPauseIconVisible(false);
                break;

            case MouseOperations.MOUSE_OVER:
                setIsMediaPauseIconVisible(true);
                onMouseHoverVideoPlayer();
                break;

            default:
        }
    }, [eventType, value]);

    useEffect(() => {
        switch (key) {
            /**
             * Both pause and play/pause buttons currently share the same functionality. If
             * future changes require different functionalities, separate functions may be needed.
             */
            case Keys.SAMSUNG_REMOTE_KEY_MEDIA_PAUSE:
            case Keys.LG_REMOTE_KEY_CODE_MEDIA_PAUSE:
            case Keys.KEYBOARD_KEY_PAUSE:
            case Keys.KEYBOARD_KEY_PLAY_PAUSE:
            case Keys.SAMSUNG_REMOTE_KEY_MEDIA_PLAY_PAUSE:
                onKeyPlayPauseVideoPlayer();
                break;
            default:
        }
    }, [identity]);

    /**
     * This useEffect handles changes in visibility state:
     * - When visibility is ON_PAUSE, playback is stopped. If Socu mode is enabled, the last viewed
     *   position is updated, and Socu data is reset.
     * - When visibility is ON_RESUME and a preserved playable exists, a new playable is fetched
     *   from the preserved channel number and live playback starts.
     */

    useEffect(() => {
        handleVisibilityChange(visibilityState);
    }, [visibilityState]);

    return (
        <React.Fragment>
            <div className="video-container minimize hidden" ref={uiContainerRef}>
                <Mousetrap value={ID}>
                    <div className={`black-surface ${!isBlackSurfaceActive ? 'hidden' : ''}`}></div>
                    <video ref={videoRef} muted={!unmuted}></video>
                    {isMediaPauseIconVisible ? <ImageLoader imageType={videoWindowIcon} /> : null}
                </Mousetrap>
            </div>
        </React.Fragment>
    );
};

export default VideoPlayer;
